


import React, { useState, useEffect, useContext } from "react";

import { useAppTheme } from "core/contexts/theme/theme";
import { AuthContext } from "core/contexts/auth/auth";
import DownloadFileIcon from '@mui/icons-material/FileDownload';
import { fetchAccounts, fetchCategories, fetchResourceTypes, fetchPolicies, fetchResource, fetchFilteredFindings, downloadPDF } from "../services/eaasservices";



export const EaasAccelerator: React.FC = () => {
  const { checkAuthToken } = useContext(AuthContext);
  const [bearerToken, setBearerToken] = useState<string>("");

  const [filters, setFilters] = useState({
    awsAccount: "",
    resourceCategory: "",
    resourceType: "",
    resource: "",
    policy: "",
    accountName: ""
  });

  const [filterOptions, setFilterOptions] = useState<{
    resourceCategories: { id?: string; name: string }[];
    resourceTypes: { id?: string; name: string }[];
    resources: { id?: string; name: string }[];
    policies: { id?: string; name: string }[];
  }>({
    resourceCategories: [], // { id, name }
    resourceTypes: [],      // { id, name }
    resources: [],          // { id, name }
    policies: [],           // { id, name }
  });

  // State for account dropdown (array of objects)
  const [accountOptions, setAccountOptions] = useState<{
    account_id: string;
    name: string;
    email: string;
  }[]>([]);

  // Get bearer token on component mount
  useEffect(() => {
    const getToken = async () => {
      const token = await checkAuthToken();
      setBearerToken(token);
    };
    getToken();
  }, [checkAuthToken]);
  // Fetch accounts for dropdown
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  useEffect(() => {
    const fetchAccountsData = async () => {
      const token = await checkAuthToken();
      setLoadingAccounts(true);
      fetchAccounts(token)
        .then((accounts) => setAccountOptions(accounts))
        .catch(() => setAccountOptions([]))
        .finally(() => setLoadingAccounts(false));
    };
    fetchAccountsData();
  }, [checkAuthToken]);

  // Loading state for combineFilters
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [currentValue, setCurrentValue] = useState("");
  const [loadingCategory, setLoadingCategory] = useState("");
  // Update filter dropdowns when account changes
  useEffect(() => {
    if (filters.awsAccount) {
      setFiltersLoading(true);
      let payload = "account=" + filters.awsAccount.split("/")[1];
      fetchCategories(payload, bearerToken)
        .then((data) => {
          setFilterOptions((prev) => ({
            ...prev,
            resourceCategories: data || [],
          }));
        })
        .catch(() => {
          setFilterOptions((prev) => ({
            ...prev,
            resourceCategories: []
          }));
        })
        .finally(() => setFiltersLoading(false));
    }
  }, [filters.awsAccount]);
  useEffect(() => {
    if (filters.awsAccount && filters.resourceCategory) {
      setLoadingCategory("resourceType");
      let payload = "account=" + filters.awsAccount.split("/")[1];
      payload += filters.resourceCategory ? "&category=" + filters.resourceCategory : "";
      fetchResourceTypes(payload, bearerToken)
        .then((data) => {
          setFilterOptions((prev) => ({
            ...prev,
            resourceTypes: data || [],

          }));
        })
        .catch(() => {
          setFilterOptions((prev) => ({
            ...prev,
            resourceTypes: [],

          }));
        })
        .finally(() => setLoadingCategory(""));
    }
  }, [filters.resourceCategory]);
  useEffect(() => {
    if (filters.awsAccount && filters.resourceCategory) {
      setLoadingCategory("policies");
      let payload = "account=" + filters.awsAccount.split("/")[1];
      payload += filters.resourceCategory ? "&resource_category=" + filters.resourceCategory : "";
      payload += filters.resourceType ? "&resource_type=" + filters.resourceType : "";
      fetchPolicies(payload, bearerToken)
        .then((data) => {
          setFilterOptions((prev) => ({
            ...prev,
            policies: data || [],
          }));
        })
        .catch(() => {
          setFilterOptions((prev) => ({
            ...prev,
            policies: [],

          }));
        })
        .finally(() => setLoadingCategory(""));
    }
  }, [filters.resourceType]);
  useEffect(() => {
    if (filters.awsAccount && filters.resourceCategory) {
      setLoadingCategory("resources");
      let payload = "account=" + filters.awsAccount.split("/")[1];
      payload += filters.resourceCategory ? "&resource_category=" + filters.resourceCategory : "";
      payload += filters.resourceType ? "&resource_type=" + filters.resourceType : "";
      fetchResource(payload, bearerToken)
        .then((data) => {
          setFilterOptions((prev) => ({
            ...prev,
            resources: data || [],
          }));
        })
        .catch(() => {
          setFilterOptions((prev) => ({
            ...prev,
            resources: [],

          }));
        })
        .finally(() => setLoadingCategory(""));
    }
  }, [filters.resourceType]);



  //filters.resourceType,filters.resourceCategory

  const statusColor = {
    FAILED: "bg-red-100 text-red-700",
    PASSED: "bg-green-100 text-green-700",
    WARNING: "bg-yellow-100 text-yellow-700",
  };
  const severityColor = {
    CRITICAL: "bg-red-600 text-white",
    HIGH: "bg-pink-500 text-white",
    MEDIUM: "bg-yellow-400 text-black",
    LOW: "bg-green-300 text-black",
  };

  interface Finding {
    resource_id: string;
    resource_type: string;
    policy_name: string;
    control_description: string;
    account_id: string;
    CreationDate: string;
    status: string;
    severity: string;
    resource_link: string;
    policy_link: string;

  }

  const [filteredFindings, setFilteredFindings] = useState<Finding[]>([]);
  const [selected, setSelected] = useState({
    policies: [],
    resources: []
  });
  const { apptheme } = useAppTheme();

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    // If account changes, reset other filters
    if (name === "awsAccount") {
      setFilters({
        awsAccount: value,
        resourceCategory: "",
        resourceType: "",
        resource: "",
        policy: "",
        accountName: value
      });
      setSelected({ policies: [], resources: [] });
    } else if (name === "resourceCategory") {
      // Reset dependent filters when category changes
      setFilters({ ...filters, resourceCategory: value, resourceType: "", resource: "" });
      setSelected({ policies: [], resources: [] });
    } else if (name === "resourceType") {
      // Reset dependent filters when resource type changes
      setFilters({ ...filters, resourceType: value, resource: "" });
      setSelected({ policies: [], resources: [] });
    }
    else if (name === "policies") {
      // Reset dependent filters when resource type changes

      setSelected(prev => ({ ...prev, resources: [] }));
    }

    else {
      setFilters({ ...filters, [name]: value });
    }
  };

  // API call for filter
  async function handleApplyFilter() {
    if (!bearerToken) return;
    setDataLoading(true);

    try {
      let payloadFilters = { ...filters, policy_names: selected.policies, resource_ids: selected.resources };
      const result = await fetchFilteredFindings(payloadFilters, bearerToken);
      setFilteredFindings(result);
    } catch (error) {
      console.error('Failed to apply filter', error);
      setFilteredFindings([]);
    } finally {
      setDataLoading(false);
    }
  }

  async function generateEvidence() {
    if (!bearerToken) return;
    try {
      setLoadingCategory("download");
      await downloadPDF({ ...filters, policy_names: selected.policies, resource_ids: selected.resources }, bearerToken);
      setLoadingCategory("");
    } catch (error) {
      console.error('Failed to generate evidence', error);
    }
    finally {
      setLoadingCategory("");
    }
  }
  const handleSelect = (key, value) => {
    if (!value) return;

    setSelected(prev => {
      if (prev[key].includes(value)) return prev; // avoid duplicates
      return { ...prev, [key]: [...prev[key], value] };
    });
    setCurrentValue("");
  };

  const handleRemove = (key, value) => {
    setSelected(prev => ({
      ...prev,
      [key]: prev[key].filter(v => v !== value),
    }));
  };

  return (
    <div
      className="flex-none p-6 font-sans bg-gray-50 text-gray-900 h-100">
      <div
        className="rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">AWS Account</label>
              <select
                name="awsAccount"
                value={filters.awsAccount}
                onChange={handleFilterChange}
                disabled={filtersLoading || loadingAccounts}
                className="w-full border rounded px-2 py-1 disabled:opacity-60"
              >
                <option value="">Select account</option>
                {accountOptions.map(acc => (
                  <option key={acc.name} value={acc.name + "/" + acc.account_id}>
                    {acc.name} ({acc.account_id})
                  </option>
                ))}
              </select>
              {(filtersLoading || loadingAccounts) && (
                <div className="flex items-center mt-1 text-xs text-blue-600">
                  <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  {loadingAccounts ? "Loading accounts..." : "Loading filters..."}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Resource Category</label>
              <select
                name="resourceCategory"
                value={filters.resourceCategory}
                onChange={handleFilterChange}
                disabled={!filters.awsAccount || filtersLoading}
                className="w-full border rounded px-2 py-1 disabled:opacity-60"
              >
                <option value="">Select category</option>
                {filterOptions.resourceCategories.map(cat => (
                  <option key={cat.id || cat.name} value={cat.id || cat.name}>{cat.name}</option>
                ))}
              </select>
              {loadingCategory === "resourceCategory" && (
                <div className="flex items-center mt-1 text-xs text-blue-600">
                  <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  Loading...
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Resource Type</label>
              <select
                name="resourceType"
                value={filters.resourceType}
                onChange={handleFilterChange}
                disabled={!filters.awsAccount || filtersLoading}
                className="w-full border rounded px-2 py-1 disabled:opacity-60"
              >
                <option value="">Select type</option>
                {filterOptions.resourceTypes.map(type => (
                  <option key={type.id || type.name} value={type.name}>{type.name}</option>
                ))}
              </select>
              {loadingCategory === "resourceType" && (
                <div className="flex items-center mt-1 text-xs text-blue-600">
                  <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  Loading...
                </div>
              )}
            </div>


            {["policies", "resources"].map((labels) => (
              <div key={labels}>

                <label className="block text-sm font-medium mb-1 capitalize">
                  {labels}
                </label>

                {/* Dropdown */}
                <select
                  className="w-full border rounded px-2 py-1 disabled:opacity-60"
                  disabled={!filters.awsAccount || filtersLoading}
                  value={currentValue}
                  name={labels}
                  onChange={(e) => handleSelect(labels, e.target.value)}
                >
                  <option value="" disabled>
                    Select {labels}
                  </option>
                  {filterOptions && filterOptions[labels] && filterOptions[labels].map(opt => (
                    <option key={opt.id} value={opt.name}>{opt.name}</option>
                  ))}
                </select>

                {/* Loader under dropdown */}
                {loadingCategory === labels && (
                  <div className="flex items-center mt-1 text-xs text-blue-600">
                    <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    Loading policies and resources...
                  </div>
                )}

                {/* 👇 Selected values shown BELOW this dropdown */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {selected[labels].map(item => (
                    <div
                      key={item}
                      className="flex items-center bg-gray-200 px-2 py-1 rounded text-sm"
                    >
                      {item}
                      <button
                        className="ml-2 text-gray-600 hover:text-red-600"
                        onClick={() => handleRemove(labels, item)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

              </div>
            ))}
            <div>
              <button
                className="mt-6 px-4 py-2 rounded font-semibold shadow transition bg-primary text-white"

                onClick={handleApplyFilter}
              >
                Apply Filters
              </button>
            </div>
          </div>

        </div>
      </div>

      <div
        className="rounded-lg shadow-md p-6"

      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Policy Findings</h2>
          <span className="text-sm" >{filteredFindings.length} findings matched your filters</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr >
                {[
                  "Resource",
                  "Type",
                  "Policy",
                  "Status",
                  "Timestamp"
                ].map((header) => (
                  <th key={header} className="px-3 py-2 text-left font-medium">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataLoading ? (
                <div className="flex justify-center items-center py-8">
                  <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  <span className="ml-2">Loading data...</span>
                </div>
              ) :

                filteredFindings.map((finding, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="px-2 py-2" ><a
                      href={finding.resource_link} target="_blank"
                      className="text-blue-600 hover:underline block w-full h-full"
                    >
                      {finding.resource_id}
                    </a></td>
                    <td className="px-3 py-2">{finding.resource_type}</td>
                    <td className="px-3 py-2">
                      <a
                        href={finding.policy_link} target="_blank"
                        className="text-blue-600 hover:underline block w-full h-full"
                      >
                        {finding.policy_name}
                      </a>
                    </td>

                    <td className="py-2">
                      <span
                        className={`inline-block text-xs font-semibold rounded-full px-3 py-1 ${statusColor[finding.status]}`}
                        style={{ minWidth: 60, textAlign: 'center', backgroundClip: 'padding-box' }}
                      >
                        {finding.status}
                      </span>
                    </td>

                    <td className="px-3 py-2">{finding.CreationDate}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-6">
          <button onClick={generateEvidence} disabled={filteredFindings.length === 0}
            className="px-5 py-2 rounded font-semibold shadow transition bg-primary text-white flex items-center gap-2"

          ><DownloadFileIcon fontSize="small" />

            Generate Evidence
            {loadingCategory === "download" && (
              <div className="flex items-center mt-1 text-xs text-blue-600">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-100"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
              </div>)}
          </button>
        </div>
      </div>
    </div>
  );
}
